/**
 * MirrorController — the Mirror transform-handle machinery (issue
 * #180) extracted from ConstructionTool (#837, epic #828). A tool
 * OWNS a controller instance and DRIVES it by composition (it is NOT
 * a base class), mirroring the `RotationController` /
 * `DeleteSliceController` pattern: the controller owns the mirror
 * hover STATE (`mirrorHover`) — the axis-candidate highlight + the
 * per-source reflected previews — while everything
 * construction-specific it needs from the owning tool (scope / scene
 * / project / commit / selection, the detached "simple mirror" flag)
 * is injected through the `MirrorHost` interface.
 *
 * The heavy geometry lives in `src/model/constructionMirror.ts`
 * (`resolveAxisSpec` / `mirrorPathData` / `deriveConstructionMirrors`);
 * only the hover / preview / commit state + handlers move here.
 *
 * Mirror is a selection-driven, no-drag gesture: `onHover` refreshes
 * the axis candidate + preview overlay, `onDown` commits whatever's
 * hovered. ConstructionTool delegates both, preserving the exact
 * original dispatch order.
 */
import paper from "paper";
import type { Scene } from "../canvas/Scene";
import {
  projectConstructionLines,
  nextConstructionMirrorName,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import {
  applyMirror,
  deriveConstructionMirrors,
  isMirrorDerived,
  mirrorPathData,
  resolveAxisSpec,
  type MirrorMatrix,
} from "../model/constructionMirror";
import {
  isOffsetDerived,
  resolveOffsetRoot,
} from "../model/constructionOffset";
import {
  findAxisCandidate,
  nearestAxisFeature,
  axisDistanceMm,
  selfAxisGap,
  GAP_REDRAW_EPS_MM,
} from "../model/mirrorAxis";
import { newId } from "../model/ids";
import { cssVar } from "../colors";
import { hasHover, type PointerKind } from "../canvas/pointerPrecision";

/** Dashed stroke / highlight colour for the live mirror preview. */
const PREVIEW_STROKE = () => cssVar("--construction-preview");

/** Bounding-box centre of a pathData string, for the self-axis gap's "away
 *  from the axis" direction. Mirrors the deriver's own helper — the preview has
 *  to compute the gap from the same reference point the commit will, or the
 *  dashed outline lands somewhere the committed line doesn't. */
function previewCenter(
  pathData: string,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  if (!pathData) return null;
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const c = { x: cp.bounds.center.x, y: cp.bounds.center.y };
    cp.remove();
    return c;
  } catch {
    return null;
  }
}

/** Everything the MirrorController needs from its owning tool. */
export interface MirrorHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  /** True in the detached "simple mirror" sub-mode (issue #344) —
   *  the commit bakes the reflected geometry then strips the link. */
  isSimpleMirror(): boolean;
}

export class MirrorController {
  /** Mirror sub-mode hover state (issue #180). Set as the cursor
   *  passes over a candidate axis (a segment or an anchor of any
   *  visible construction line). Drives the highlight overlay and
   *  the in-place preview of the reflected geometry that the user
   *  will commit on click. Cleared on every sub-mode switch /
   *  pointer leave / commit / cancel. */
  private mirrorHover: {
    axis: NonNullable<ConstructionLine["mirrorSource"]>["axis"];
    /** True when the axis came from the "mirror that way" fallback (#343) —
     *  the cursor is out in open space and the axis is a feature of the
     *  SELECTION ITSELF. The normal scan never returns one of those, and both
     *  the preview and the commit refuse a self-axis unless this says the user
     *  aimed for it. */
    selfAimed: boolean;
    /** The self-axis gap baked into the previewed geometry (#343 follow-up).
     *  The commit stores THIS rather than recomputing from a cursor position it
     *  never saw, so what lands is exactly what was on screen. */
    gapMm: number;
    /** Highlight item drawn over the candidate axis (yellow ring
     *  for an anchor pivot, thick yellow segment for a segment
     *  axis). Ephemeral — wiped every per-frame refresh. */
    axisHighlight: paper.Item | null;
    /** Per-source preview paths (one per currently-selected source
     *  construction line). Wiped every per-frame refresh. */
    previewItems: paper.Item[];
  } | null = null;

  constructor(private host: MirrorHost) {}

  /** Test hook: current Mirror sub-tool hover axis (or `null` if no
   *  candidate). Exposed via `__editor.getMirrorHoverAxis` so e2e
   *  specs can assert which axis the cursor's resolving without
   *  parsing the rendered overlay items. */
  getMirrorHoverAxis():
    | NonNullable<ConstructionLine["mirrorSource"]>["axis"]
    | null {
    return this.mirrorHover?.axis ?? null;
  }

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** onHover delegation: recompute the axis candidate + preview. */
  handleHover(p: { x: number; y: number }): void {
    this.refreshMirrorHover(p);
  }

  /**
   * onDown delegation. A mouse COMMITS here: it has already hovered, so the
   * highlight and the dashed preview showed what this click would do before it
   * landed. No-op when nothing is hovered, so a stray click on empty canvas
   * produces nothing.
   *
   * A finger or a stylus has no hover to have shown it anything (#40), so
   * pressing would commit blind. For those the press only PREVIEWS — the
   * refresh below builds the same overlay a cursor would have — and the commit
   * waits for {@link handleUp}, with the drag in between to adjust. Same
   * gesture either way: look, then decide.
   */
  handleDown(p: { x: number; y: number }, kind?: PointerKind): void {
    this.refreshMirrorHover(p);
    if (!hasHover(kind)) return;
    if (this.mirrorHover) {
      this.commitMirrorFromHover();
    }
  }

  /** onMove delegation: while a hover-less pointer is dragging, the preview
   *  follows it, so you can see the axis change before letting go. Returns
   *  true when it handled the move. */
  handleMove(p: { x: number; y: number }, kind?: PointerKind): boolean {
    if (hasHover(kind)) return false;
    this.refreshMirrorHover(p);
    return true;
  }

  /** onUp delegation: the commit for a pointer that could not hover. Returns
   *  true when it claimed the release. */
  handleUp(p: { x: number; y: number }, kind?: PointerKind): boolean {
    if (hasHover(kind)) return false;
    this.refreshMirrorHover(p);
    if (this.mirrorHover) this.commitMirrorFromHover();
    return true;
  }

  /** Mirror sub-mode (#180): drop the hover overlay + every per-
   *  source preview. Called from cancelDraft / sub-mode switches /
   *  pointer leave / immediately after a commit. */
  clear(): void {
    if (!this.mirrorHover) return;
    if (this.mirrorHover.axisHighlight) {
      this.mirrorHover.axisHighlight.remove();
    }
    for (const item of this.mirrorHover.previewItems) {
      item.remove();
    }
    this.mirrorHover = null;
  }

  // -------------------------------------------------------------------
  // Axis candidate / closure
  // -------------------------------------------------------------------

  /** Search every visible non-derived construction line for the
   *  anchor or segment closest to `cursor`. The scan itself is the
   *  shared `findAxisCandidate` (`mirrorAxis.ts`, shared with the
   *  leather Mirror sub-tool #973) — anchors win over segments, and
   *  curved segments are skipped; this method only decides WHICH
   *  lines are eligible. Returns null when no candidate is within
   *  the radius (or when the current selection has no sources to
   *  mirror). */
  private findMirrorAxisCandidate(cursor: { x: number; y: number }): {
    axis: NonNullable<ConstructionLine["mirrorSource"]>["axis"];
    selfAimed: boolean;
  } | null {
    // Mirror is a selection-driven gesture — without a source set
    // there's nothing to project, so we don't highlight candidates
    // either (avoids the user "picking an axis" with no effect).
    const sourceIds = this.host.getScene().getSelection().constructionIds ?? [];
    if (sourceIds.length === 0) return null;
    const lines = projectConstructionLines(this.host.getProject());
    if (lines.length === 0) return null;
    const sourceSet = new Set(sourceIds);
    // The line being mirrored is allowed as its own axis (point
    // mirror onto self → degenerate but harmless). Mirroring across
    // a derived line's own axis line would create cycles in the next
    // commit, so exclude derived lines from axis candidacy entirely.
    // Source lines (currently selected) are also bad axis candidates —
    // reflecting a shape across itself is a no-op or near-no-op and
    // just confuses the gesture.
    const eligible = lines
      .filter(
        (l) =>
          l.visible !== false &&
          !sourceSet.has(l.id) &&
          !isOffsetDerived(l) &&
          !isMirrorDerived(l),
      )
      .map((l) => ({ key: l.id, pathData: l.pathData }));
    const ref = findAxisCandidate(eligible, cursor, this.host.getScope());
    if (ref) {
      return {
        axis: {
          kind: ref.kind,
          constructionId: ref.key,
          pathIndex: ref.pathIndex,
          segmentIndex: ref.segmentIndex,
        },
        selfAimed: false,
      };
    }
    const aimed = this.aimedSelfFeature(lines, sourceSet, cursor);
    if (!aimed) return null;
    return {
      axis: {
        kind: aimed.kind,
        constructionId: aimed.key,
        pathIndex: aimed.pathIndex,
        segmentIndex: aimed.segmentIndex,
      },
      selfAimed: true,
    };
  }

  /**
   * The "mirror that way" fallback (#343): with nothing lit up under the
   * cursor, the nearest feature of the SELECTION ITSELF is the axis the user
   * means — pointing past a side is enough to say which way to reflect.
   * Straight out from an edge, that edge beats both its corners and you get a
   * reflection beside the original; diagonally past a corner, the corner wins
   * and you get the 180° point mirror, which is the same shape the aim has.
   *
   * The leather Mirror sub-tool has worked this way since #973 — this is the
   * same `nearestAxisFeature` scan over construction geometry, which is the
   * whole point of that function living in the shared module.
   *
   * Only offered while the cursor is OUTSIDE the selection's bounding box:
   * "towards the mouse" is a direction, and from inside the shape there isn't
   * one. (The leather twin guards with "is the cursor over a part", which reads
   * the same way for filled outlines but says nothing about an open
   * construction line.)
   */
  private aimedSelfFeature(
    lines: ReadonlyArray<ConstructionLine>,
    sourceSet: ReadonlySet<string>,
    cursor: { x: number; y: number },
  ): ReturnType<typeof nearestAxisFeature> {
    const scope = this.host.getScope();
    const own = lines
      .filter((l) => sourceSet.has(l.id) && l.visible !== false)
      .map((l) => ({ key: l.id, pathData: l.pathData }));
    if (own.length === 0) return null;
    if (this.cursorInsideOwnBounds(own, cursor, scope)) return null;
    return nearestAxisFeature(own, cursor, scope);
  }

  /** Whether `cursor` sits within the combined bounding box of the selection's
   *  own geometry. */
  private cursorInsideOwnBounds(
    own: ReadonlyArray<{ pathData: string }>,
    cursor: { x: number; y: number },
    scope: paper.PaperScope,
  ): boolean {
    let bounds: paper.Rectangle | null = null;
    for (const o of own) {
      if (!o.pathData) continue;
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({ pathData: o.pathData, insert: false });
      } catch {
        continue;
      }
      bounds = bounds ? bounds.unite(cp.bounds) : cp.bounds.clone();
      cp.remove();
    }
    return bounds
      ? bounds.contains(new scope.Point(cursor.x, cursor.y))
      : false;
  }

  /** Resolve the set of construction-line ids that will be mirrored
   *  for the current selection. The closure includes (a) every
   *  selected line, (b) the offset ROOT of any selected derived
   *  line (so mirroring an offset also mirrors its source), and
   *  (c) every offset descendant of every line in (a)+(b) — so the
   *  user's full modifier chain rides along. Shared by both the live
   *  hover preview and the commit so what the user SEES on hover is
   *  exactly what lands on click. */
  private resolveMirrorClosure(
    proj: Project,
    selectedIds: ReadonlyArray<string>,
  ): Set<string> {
    if (selectedIds.length === 0) return new Set();
    // Walk every selected derived line back to its offset root so a
    // mirror of "the offset" also mirrors its source. resolveOffsetRoot
    // is cycle-safe and returns the input id when the line isn't
    // derived.
    //
    // We mirror ONLY the non-offset root; the sibling invariant in
    // `enforceMirrorSiblingInvariant` then auto-creates
    // `mirror(offset, axis)` siblings for each offset descendant of
    // the source. Mirror is a cheap closed-form transform, so this
    // pays the paperjs-offset cost once on the original geometry
    // and replays the mirror through every axis.
    const roots = new Set<string>();
    for (const id of selectedIds) {
      roots.add(resolveOffsetRoot(proj, id));
    }
    return roots;
  }

  // -------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------

  /** Recompute the mirror hover state from the cursor position and
   *  redraw the highlight + per-source preview overlays. Called per
   *  pointer-hover frame while the Mirror sub-mode is active. */
  private refreshMirrorHover(cursor: { x: number; y: number }) {
    const picked = this.findMirrorAxisCandidate(cursor);
    const candidate = picked?.axis ?? null;
    // Cheap early-out: if the hovered axis hasn't changed, leave
    // the existing overlay alone so we don't thrash on every mouse
    // move.
    const scope = this.host.getScope();
    const axisSpec = candidate
      ? resolveAxisSpec(this.host.getProject(), candidate, scope)
      : null;
    // Across the selection's OWN edge the copy would land flush on top of it,
    // so it is pushed clear by the cursor's distance from the axis — which puts
    // its leading edge under the pointer. Across ANOTHER line's axis there is no
    // gap: butting up against that line is the point of aiming at it. Same rule,
    // same helper, as the leather Mirror sub-tool (#973).
    const gapMm =
      picked?.selfAimed && axisSpec ? axisDistanceMm(axisSpec, cursor) : 0;
    // Cheap early-out: if neither the hovered axis NOR the gap has moved, leave
    // the existing overlay alone so we don't thrash on every mouse move. The
    // gap has to be in this test — it tracks the cursor, so a plain slide away
    // from the edge is a real change even though the axis is the same one.
    const current = this.mirrorHover;
    const sameAxis =
      current &&
      candidate &&
      current.axis.constructionId === candidate.constructionId &&
      current.axis.pathIndex === candidate.pathIndex &&
      current.axis.segmentIndex === candidate.segmentIndex &&
      current.axis.kind === candidate.kind &&
      Math.abs(current.gapMm - gapMm) < GAP_REDRAW_EPS_MM;
    if (sameAxis) return;
    this.clear();
    if (!candidate || !picked || !axisSpec) return;
    // Build the highlight: yellow ring for an anchor pivot, thick
    // yellow segment for a segment axis.
    let axisHighlight: paper.Item | null = null;
    const zoom = scope.view.zoom > 0 ? scope.view.zoom : 1;
    if (candidate.kind === "point") {
      const ring = new scope.Path.Circle(
        new scope.Point(axisSpec.origin.x, axisSpec.origin.y),
        6 / zoom,
      );
      ring.strokeColor = new scope.Color(PREVIEW_STROKE());
      ring.strokeWidth = 2;
      ring.strokeScaling = false;
      ring.fillColor = null;
      ring.data = { ephemeral: true };
      axisHighlight = ring;
    } else {
      const dir = axisSpec.dir!;
      const len = Math.hypot(dir.x, dir.y);
      if (len > 1e-6) {
        const end = {
          x: axisSpec.origin.x + dir.x,
          y: axisSpec.origin.y + dir.y,
        };
        const seg = new scope.Path.Line(
          new scope.Point(axisSpec.origin.x, axisSpec.origin.y),
          new scope.Point(end.x, end.y),
        );
        seg.strokeColor = new scope.Color(PREVIEW_STROKE());
        seg.strokeWidth = 3;
        seg.strokeScaling = false;
        seg.data = { ephemeral: true };
        axisHighlight = seg;
      }
    }
    // Build the per-source preview reflections.
    const matrix: MirrorMatrix = (() => {
      // Inline the matrix build instead of re-exporting it from the
      // helper module — we need it here for previews anyway.
      if (axisSpec.kind === "point") {
        return {
          m11: -1,
          m12: 0,
          tx: 2 * axisSpec.origin.x,
          m21: 0,
          m22: -1,
          ty: 2 * axisSpec.origin.y,
        };
      }
      const dir = axisSpec.dir!;
      const len = Math.hypot(dir.x, dir.y);
      const ux = dir.x / len;
      const uy = dir.y / len;
      const a = 2 * ux * ux - 1;
      const b = 2 * ux * uy;
      const c = 2 * uy * uy - 1;
      return {
        m11: a,
        m12: b,
        tx: axisSpec.origin.x - a * axisSpec.origin.x - b * axisSpec.origin.y,
        m21: b,
        m22: c,
        ty: axisSpec.origin.y - b * axisSpec.origin.x - c * axisSpec.origin.y,
      };
    })();
    const sourceIds = this.host.getScene().getSelection().constructionIds ?? [];
    const lines = projectConstructionLines(this.host.getProject());
    const byId = new Map(lines.map((l) => [l.id, l] as const));
    // Use the same closure the commit will: every selected line, its
    // offset root (if it's derived), and the offset descendants of
    // those roots. The preview now shows the entire reflected
    // chain — not just the selected lines — so what the user sees
    // is exactly what lands on click.
    const closure = this.resolveMirrorClosure(
      this.host.getProject(),
      sourceIds,
    );
    const previewItems: paper.Item[] = [];
    for (const sid of closure) {
      // Skip lines whose own anchor / segment IS the axis — unless the user
      // AIMED at it (#343), where reflecting the shape across its own edge is
      // precisely the "mirror that way" gesture and lands the copy beside the
      // original.
      if (sid === candidate.constructionId && !picked.selfAimed) continue;
      const src = byId.get(sid);
      if (!src) continue;
      // Fold the gap into this source's translation, the same way
      // `deriveConstructionMirrors` will on commit — so the dashed preview IS
      // the result rather than an approximation of it.
      let m = matrix;
      if (gapMm > 0) {
        const centre = previewCenter(src.pathData, scope);
        if (centre) {
          const g = selfAxisGap(axisSpec, centre, gapMm);
          m = { ...matrix, tx: matrix.tx + g.dx, ty: matrix.ty + g.dy };
        }
      }
      const mirroredPath = mirrorPathData(src.pathData, m, scope);
      if (!mirroredPath) continue;
      try {
        const preview = new scope.CompoundPath({
          pathData: mirroredPath,
          insert: true,
        });
        preview.strokeColor = new scope.Color(PREVIEW_STROKE());
        preview.strokeWidth = 1.6;
        preview.strokeScaling = false;
        preview.dashArray = [4, 3];
        preview.fillColor = null;
        preview.data = { ephemeral: true };
        previewItems.push(preview);
      } catch {
        /* ignore — preview only */
      }
    }
    this.mirrorHover = {
      axis: candidate,
      selfAimed: picked.selfAimed,
      gapMm,
      axisHighlight,
      previewItems,
    };
    // Silence the "applyMirror is imported but unused" hint — we
    // keep the import available so future preview consumers don't
    // have to re-add it. (The matrix above is inlined for one
    // call site; applyMirror is the per-point variant that other
    // callers may need.)
    void applyMirror;
  }

  // -------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------

  /** Commit one derived mirror line per currently-selected source
   *  PLUS one mirror line per offset descendant of each selected
   *  source — so the user's modifiers (offset chain) ride along
   *  with the mirror, the same way they do with Duplicate (issue
   *  #180 follow-up). Skips silently when no candidate is hovered
   *  or no sources are selected so a stray click on empty canvas
   *  doesn't produce anything. */
  private commitMirrorFromHover() {
    if (!this.mirrorHover) return;
    const axis = this.mirrorHover.axis;
    const selfAimed = this.mirrorHover.selfAimed;
    const gapMm = this.mirrorHover.gapMm;
    const sourceIds = this.host.getScene().getSelection().constructionIds ?? [];
    if (sourceIds.length === 0) return;
    // Simple mirror (issue #344): produce a STANDALONE reflected line —
    // as if the live modifier were created then detached immediately.
    // We still build the linked line, run the real mirror deriver to
    // bake its mirrored geometry (so the axis resolution matches the
    // modifier exactly), then strip `mirrorSource` before the commit
    // returns. The live "modifier" mirror keeps the link.
    const detached = this.host.isSimpleMirror();
    const scope = this.host.getScope();
    this.host.commit(
      detached
        ? "Mirror construction lines (detached)"
        : "Mirror construction lines",
      (proj) => {
        const lines = projectConstructionLines(proj);
        const byId = new Map(lines.map((l) => [l.id, l] as const));
        // Use the same closure the live preview used: every selected
        // line, the offset ROOT of any selected derived line (so
        // mirroring an offset also mirrors its source — issue #180
        // follow-up), plus every offset descendant of those roots so
        // the user's modifier chain rides along, same as Duplicate.
        // Each descendant becomes a free-standing mirror line whose
        // own `mirrorSource` points back at its original, so when
        // the original's deriver moves the source, the descendant's
        // mirror follows on the next commit.
        const toMirror = this.resolveMirrorClosure(proj, sourceIds);
        const newLines: ConstructionLine[] = [];
        const newIds = new Set<string>();
        for (const oldId of toMirror) {
          const src = byId.get(oldId);
          if (!src) continue;
          // Refuse to mirror onto self — the axis normally can't belong to
          // the same line the user is mirroring. The hover-candidate filter
          // already excludes selected sources, but a stale commit could still
          // see one — be defensive. The exception is the "mirror that way"
          // fallback (#343), where a feature of the selection IS the axis by
          // design: the copy lands beside the original, hinged on the edge the
          // cursor pointed past. `deriveConstructionMirrors` resolves the axis
          // from the source's own geometry, so the pair moves together.
          if (axis.constructionId === oldId && !selfAimed) continue;
          const id = newId("clin");
          newIds.add(id);
          newLines.push({
            id,
            name: nextConstructionMirrorName(
              { ...proj, constructionLines: [...lines, ...newLines] },
              src,
            ),
            // pathData seeded with the source's geometry — the
            // commit pipeline's `deriveConstructionMirrors` will
            // immediately overwrite it with the mirrored path on
            // the same commit. Seeding it (rather than `""`) keeps
            // the line addressable if anything downstream tries to
            // read it before the deriver runs.
            pathData: src.pathData,
            mirrorSource: {
              sourceId: src.id,
              axis,
              // Stored, not baked: the mirror is a LIVE link, so the gap has to
              // survive every re-derive and travel with the pair when the
              // source moves (#343).
              ...(gapMm > 0 ? { gapMm } : {}),
            },
          });
        }
        if (newLines.length === 0) return proj;
        let next: typeof proj = {
          ...proj,
          constructionLines: [...lines, ...newLines],
        };
        if (detached) {
          // Bake the mirrored geometry, then cut the link so the new
          // lines stand alone (the outer pipeline's mirror pass then
          // finds nothing to derive for them).
          next = deriveConstructionMirrors(next, scope);
          next = {
            ...next,
            constructionLines: projectConstructionLines(next).map((l) => {
              if (!newIds.has(l.id)) return l;
              const { mirrorSource: _drop, ...rest } = l;
              return rest;
            }),
          };
        }
        return next;
      },
    );
    // Stay in mirror mode so the user can keep firing on more axes
    // (matches the offset sub-mode's "click-click-click" pattern).
    // But the hover overlay needs a refresh from the next pointer
    // event since the model just changed — clearing it now triggers
    // a fresh redraw the moment the cursor moves.
    this.clear();
  }
}
